import React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaPlus, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { budgetService, budgetCategoryService, incomeService, isValidObjectId } from '../../../services/apiService';
import { useAuthContext } from '../../../features/auth/context/AuthContext';

// ErrorBoundary for catching render errors
function ErrorBoundary({ children }) {
  const [error, setError] = useState(null);
  if (error) {
    return <div style={{ color: 'red', padding: 24 }}>
      <h2>Something went wrong in BudgetForm:</h2>
      <pre>{error.message}</pre>
      <pre>{error.stack}</pre>
    </div>;
  }
  return (
    <ErrorCatcher onError={setError}>{children}</ErrorCatcher>
  );
}

class ErrorCatcher extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    if (this.props.onError) this.props.onError(error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: 'red', padding: 24, background: '#fff0f0', border: '1px solid #f00' }}>
          <h2>Something went wrong in BudgetForm (ErrorCatcher):</h2>
          <pre>{this.state.error?.message || 'Unknown error'}</pre>
          <pre>{this.state.error?.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Default categories to create if none exist - Now using the service's default categories
const BudgetForm = ({ onSuccess, onCancel, budget = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const isEditMode = Boolean(id) || Boolean(budget);
  const budgetId = id || budget?._id;
  
  // Check if user has a partner
  const hasPartner = !!(user?.partner || user?.partnerId || user?.createdPartner);
  
  // Debug log for mode
  console.log('[BudgetForm] TOP-LEVEL RENDER. isEditMode:', isEditMode, 'id:', id, 'budget:', budget, 'hasPartner:', hasPartner);

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [existingIncomes, setExistingIncomes] = useState([]);
  const [showIncomeSelector, setShowIncomeSelector] = useState(false);
  const [creatingCategoryIndex, setCreatingCategoryIndex] = useState(-1);
  const [inlineNewCategoryName, setInlineNewCategoryName] = useState('');

  const [categoriesPopulated, setCategoriesPopulated] = useState(false);
  const [incomesPopulated, setIncomesPopulated] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPrimary: false,
    isActive: true,
    period: {
      type: 'monthly',
      startDate: new Date().toISOString().slice(0, 10),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().slice(0, 10)
    },
    categories: [],
    income: []
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await budgetCategoryService.getAllCategories();
        
        let categoriesArray = Array.isArray(fetchedCategories) ? fetchedCategories : [];
        console.log('[BudgetForm] Fetched categories from API:', categoriesArray);
        
        setCategories(categoriesArray);
        
      } catch (error) {
        console.error('Error fetching budget categories:', error);
        toast.error('Failed to load categories. Check connection or server status.');
      }
    };

    const fetchIncomes = async () => {
      try {
        // Fetch individual income sources - include partner income if user has a partner
        const fetchedIncomesResponse = await incomeService.getAllIncomes({ 
          includePartner: hasPartner 
        });
        console.log('📬 [BudgetForm] Raw response from incomeService.getAllIncomes:', fetchedIncomesResponse, 'includePartner:', hasPartner);
        
        // Handle different response formats
        let incomesArray = [];
        if (fetchedIncomesResponse?.data && Array.isArray(fetchedIncomesResponse.data)) {
          incomesArray = fetchedIncomesResponse.data;
        } else if (Array.isArray(fetchedIncomesResponse)) {
          incomesArray = fetchedIncomesResponse;
        }
        
        console.log(`📬 Found ${incomesArray.length} income sources`);
        
        // Fetch income buckets - include partner buckets if user has a partner
        const fetchedBuckets = await incomeService.getIncomeBuckets({ 
          includePartner: hasPartner 
        });
        console.log('🪣 [BudgetForm] Fetched income buckets:', fetchedBuckets, 'includePartner:', hasPartner);
        
        // Process each bucket to add calculated properties
        const processedBuckets = (Array.isArray(fetchedBuckets) ? fetchedBuckets : []).map(bucket => {
          // Calculate number of incomes in this bucket
          const matchingIncomes = incomesArray.filter(income => 
            income.bucket && 
            (income.bucket === bucket._id || 
             (income.bucket._id && income.bucket._id === bucket._id) || 
             income.bucketId === bucket._id)
          );
          
          const incomesCount = matchingIncomes.length;
          
          // Calculate the total monthly amount from matching incomes
          const totalMonthlyAmount = matchingIncomes.reduce(
            (sum, income) => sum + (income.amount || 0), 
            0
          );
          
          return {
            ...bucket,
            type: 'bucket',
            amount: totalMonthlyAmount > 0 ? totalMonthlyAmount : (bucket.totalMonthlyIncome || 0),
            netAmount: (totalMonthlyAmount > 0 ? totalMonthlyAmount : (bucket.totalMonthlyIncome || 0)) * 0.75,
            periodicity: 'monthly',
            category: 'BUCKET',
            incomesCount: incomesCount,
            // Include the matching incomes pre-filtered
            matchingIncomes: matchingIncomes
          };
        });
        
        // Combine individual incomes and buckets
        const combinedIncomes = [...incomesArray, ...processedBuckets];
        console.log('🔄 [BudgetForm] Combined income sources and buckets:', combinedIncomes);
        
        // Store fetched incomes for the selector modal
        setExistingIncomes(combinedIncomes);
        
      } catch (error) {
        console.error('Error fetching incomes or buckets:', error);
        toast.error('Failed to load income sources. Please try again.');
        setExistingIncomes([]); // Ensure it's empty on error
      }
    };

    const fetchBudget = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        
        // If budget is passed as prop, use it directly, otherwise fetch from API
        let data;
        if (budget) {
          data = budget;
          console.log('Using budget from props:', data);
        } else {
          data = await budgetService.getBudgetById(budgetId);
          console.log('Fetched budget data:', data);
        }
        
        // Format dates for form inputs
        const formattedBudget = {
          ...data,
          period: {
            type: data.period?.type || 'monthly',
            startDate: data.period?.startDate ? new Date(data.period.startDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
            endDate: data.period?.endDate ? new Date(data.period.endDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)
          },
          categories: Array.isArray(data.categories) ? data.categories.map(cat => ({
            categoryId: cat.categoryId,
            name: cat.name,
            frequency: cat.frequency || 'monthly',
            plannedAmount: cat.plannedAmount || 0,
            actualAmount: cat.actualAmount || 0,
            rollover: cat.rollover || false,
            notes: cat.notes || '',
            subcategories: cat.subcategories || []
          })) : [],
          income: Array.isArray(data.income) ? data.income.map(inc => ({
            sourceId: inc.sourceId,
            name: inc.name,
            plannedAmount: inc.plannedAmount || 0,
            netAmount: inc.netAmount || (inc.plannedAmount * 0.75),
            actualAmount: inc.actualAmount || 0,
            type: inc.type || 'individual',
            periodicity: inc.periodicity || 'monthly'
          })) : []
        };
        
        console.log('Formatted budget data:', formattedBudget);
        setFormData(formattedBudget);
        setCategoriesPopulated(true);
      } catch (error) {
        console.error('Error loading budget:', error);
        toast.error('Failed to load budget data');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
    fetchIncomes();
    fetchBudget();
  }, [budgetId, isEditMode, categoriesPopulated, incomesPopulated, budget]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleAddCategory = () => {
    setFormData({
      ...formData,
      categories: [
        ...formData.categories,
        {
          categoryId: null,  // Initialize with null instead of a string
          name: '',
          frequency: 'monthly',
          plannedAmount: 0,
          actualAmount: 0,
          rollover: false,
          notes: '',
          subcategories: []
        }
      ]
    });
  };

  const handleRemoveCategory = (index) => {
    const updatedCategories = [...formData.categories];
    updatedCategories.splice(index, 1);
    setFormData({
      ...formData,
      categories: updatedCategories
    });
    // If we remove the row currently being created, reset creation mode
    if (index === creatingCategoryIndex) {
      setCreatingCategoryIndex(-1);
      setInlineNewCategoryName('');
    }
  };

  const handleIncomeChange = (index, e) => {
    const { name, value } = e.target;
    const newIncomes = [...formData.income];
    
    // Update the specified field
    newIncomes[index] = {
      ...newIncomes[index],
      [name]: name === 'plannedAmount' ? parseFloat(value) || 0 : value
    };
    
    setFormData({
      ...formData,
      income: newIncomes
    });
  };

  const handleRemoveIncome = (index) => {
    const updatedIncome = [...formData.income];
    updatedIncome.splice(index, 1);
    
    setFormData({
      ...formData,
      income: updatedIncome
    });
  };

  const handleCategoryChange = (index, name, value) => {
    const updatedCategories = [...formData.categories];
    
    if (name === 'categoryId') {
      if (value === 'create-new') {
        setCreatingCategoryIndex(index);
        setInlineNewCategoryName('');
        return;
      }
      
      setCreatingCategoryIndex(-1);
      const selectedCategory = categories.find(cat => cat._id === value);
      if (selectedCategory) {
        updatedCategories[index] = {
          ...updatedCategories[index],
          categoryId: selectedCategory._id,
          name: selectedCategory.name,
          plannedAmount: updatedCategories[index].plannedAmount || 0,
          actualAmount: updatedCategories[index].actualAmount || 0,
          rollover: updatedCategories[index].rollover || false,
          frequency: updatedCategories[index].frequency || 'monthly'
        };
      }
    } else if (name === 'plannedAmount' || name === 'actualAmount') {
      updatedCategories[index] = {
        ...updatedCategories[index],
        [name]: parseFloat(value) || 0
      };
    } else if (name === 'rollover') {
      updatedCategories[index] = {
        ...updatedCategories[index],
        rollover: Boolean(value)
      };
    } else {
      updatedCategories[index] = {
        ...updatedCategories[index],
        [name]: value
      };
    }

    setFormData({
      ...formData,
      categories: updatedCategories
    });
  };

  const handleSaveNewCategory = async () => {
    if (!inlineNewCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    if (creatingCategoryIndex < 0) return; // Should not happen

    try {
      setLoading(true);
      const randomColor = `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`;
      
      const createdCategory = await budgetCategoryService.createCategory({
        name: inlineNewCategoryName,
        type: 'expense',
        icon: 'tag',
        color: randomColor
      });

      // Add to the main categories list for the dropdown
      setCategories(prevCategories => [...prevCategories, createdCategory]);

      // Update the specific row in formData
      const updatedFormDataCategories = [...formData.categories];
      updatedFormDataCategories[creatingCategoryIndex] = {
        ...updatedFormDataCategories[creatingCategoryIndex],
        categoryId: createdCategory._id,
        name: createdCategory.name
      };
      
      setFormData(prev => ({
        ...prev,
        categories: updatedFormDataCategories
      }));

      // Reset state
      toast.success('Category created and selected');
      setCreatingCategoryIndex(-1);
      setInlineNewCategoryName('');

    } catch (error) {
      console.error('Error creating inline category:', error);
      toast.error('Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNewCategory = () => {
    if (creatingCategoryIndex < 0) return;

    // Check if the row being canceled was a newly added blank row
    const categoryBeingCreated = formData.categories[creatingCategoryIndex];
    if (!categoryBeingCreated.categoryId && !categoryBeingCreated.name && categoryBeingCreated.plannedAmount === 0) {
       // If it was a blank row (no ID, no name, 0 planned), remove it
       handleRemoveCategory(creatingCategoryIndex);
    } else {
       // Otherwise, just exit creation mode, leaving the row as it was
       setCreatingCategoryIndex(-1);
       setInlineNewCategoryName('');
    }
  };

  const handleAddIncome = () => {
    // Show the income selector modal instead of directly adding a blank income
    setShowIncomeSelector(true);
  };

  const handleAddCustomIncome = () => {
    setFormData({
      ...formData,
      income: [
        ...formData.income,
        {
          name: 'New Income Source',
          plannedAmount: 0,
          actualAmount: 0
        }
      ]
    });
    setShowIncomeSelector(false);
  };

  const handleSelectExistingIncome = async (selectedIncome) => {
    console.log(`🖱️ [BudgetForm] handleSelectExistingIncome called with:`, selectedIncome);
    
    if (selectedIncome.type === 'bucket') {
      // Start loading state
      setLoading(true);
      
      try {
        // If we already have the matching incomes from our initial fetch, use those
        let bucketIncomes = selectedIncome.matchingIncomes || [];
        
        // If we don't have pre-calculated matching incomes, get them from the API
        if (!bucketIncomes || bucketIncomes.length === 0) {
          bucketIncomes = await incomeService.getIncomesByBucket(selectedIncome._id);
          console.log(`🪣 [BudgetForm] Fetched incomes for bucket ${selectedIncome.name}:`, bucketIncomes);
        }
        
        // Check if we have any incomes in this bucket
        if (Array.isArray(bucketIncomes) && bucketIncomes.length > 0) {
          // Add all incomes from the bucket to the budget
          setFormData(prevFormData => {
            const newIncomeList = [
              ...prevFormData.income,
              ...bucketIncomes.map(income => ({
                sourceId: income._id,
                name: income.name,
                plannedAmount: income.amount || 0,
                netAmount: income.netAmount || (income.amount * 0.75),
                actualAmount: income.actualAmount || income.netAmount || (income.amount * 0.75),
                type: 'individual',
                periodicity: income.periodicity || income.frequency || 'monthly',
                bucketId: selectedIncome._id,
                bucketName: selectedIncome.name
              }))
            ];
            return {
              ...prevFormData,
              income: newIncomeList
            };
          });
          
          toast.success(`Added ${bucketIncomes.length} income sources from "${selectedIncome.name}" bucket`);
        } else {
          // If no incomes found or error occurred, just add the bucket as a single income source
          console.log(`No incomes found in bucket, adding bucket itself as income source`);
          setFormData(prevFormData => {
            const newIncomeList = [
              ...prevFormData.income,
              {
                sourceId: selectedIncome._id,
                name: `${selectedIncome.name} (Bucket Total)`,
                plannedAmount: selectedIncome.amount || 0,
                netAmount: selectedIncome.netAmount || (selectedIncome.amount * 0.75),
                actualAmount: selectedIncome.actualAmount || selectedIncome.netAmount || (selectedIncome.amount * 0.75),
                type: 'bucket',
                periodicity: 'monthly',
                bucketId: selectedIncome._id,
                bucketName: selectedIncome.name
              }
            ];
            return {
              ...prevFormData,
              income: newIncomeList
            };
          });
          
          toast.success(`Added "${selectedIncome.name}" bucket as an income source`);
        }
      } catch (error) {
        console.error(`Error handling bucket selection for ${selectedIncome.name}:`, error);
        
        // Fallback: Add the bucket as a single income source
        setFormData(prevFormData => {
          const newIncomeList = [
            ...prevFormData.income,
            {
              sourceId: selectedIncome._id,
              name: `${selectedIncome.name} (Bucket Total)`,
              plannedAmount: selectedIncome.amount || 0,
              netAmount: selectedIncome.netAmount || (selectedIncome.amount * 0.75),
              actualAmount: selectedIncome.actualAmount || selectedIncome.netAmount || (selectedIncome.amount * 0.75),
              type: 'bucket',
              periodicity: 'monthly',
              bucketId: selectedIncome._id,
              bucketName: selectedIncome.name
            }
          ];
          return {
            ...prevFormData,
            income: newIncomeList
          };
        });
        
        toast.success(`Added "${selectedIncome.name}" bucket as an income source`);
      } finally {
        // End loading state
        setLoading(false);
      }
    } else {
      // This is an individual income source
      setFormData(prevFormData => {
        const newIncomeList = [
          ...prevFormData.income,
          {
            sourceId: selectedIncome._id,
            name: selectedIncome.name,
            plannedAmount: selectedIncome.amount || 0,
            netAmount: selectedIncome.netAmount || (selectedIncome.amount * 0.75),
            actualAmount: selectedIncome.actualAmount || selectedIncome.netAmount || (selectedIncome.amount * 0.75),
            type: 'individual',
            periodicity: selectedIncome.periodicity || selectedIncome.frequency || 'monthly',
            bucketId: selectedIncome.bucketId,
            bucketName: selectedIncome.bucketName
          }
        ];
        return {
          ...prevFormData,
          income: newIncomeList
        };
      });
      
      toast.success(`Added ${selectedIncome.name}`);
    }
    
    // Close the income selector
    setShowIncomeSelector(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Filter out categories without valid IDs and ensure all required fields
      const validCategories = formData.categories
        .filter(cat => cat.categoryId && isValidObjectId(cat.categoryId))
        .map(cat => ({
          categoryId: cat.categoryId,
          name: cat.name,
          frequency: cat.frequency || 'monthly',
          plannedAmount: parseFloat(cat.plannedAmount) || 0,
          actualAmount: parseFloat(cat.actualAmount) || 0,
          rollover: Boolean(cat.rollover),
          notes: cat.notes || '',
          subcategories: cat.subcategories || []
        }));

      // Calculate total planned expenses
      const totalPlannedExpenses = validCategories.reduce((sum, cat) => 
        sum + parseFloat(cat.plannedAmount), 0);

      // Ensure income data is properly formatted
      const formattedIncome = formData.income.map(inc => ({
        sourceId: inc.sourceId,
        name: inc.name,
        plannedAmount: parseFloat(inc.plannedAmount) || 0,
        netAmount: parseFloat(inc.netAmount) || parseFloat(inc.plannedAmount) * 0.75,
        actualAmount: parseFloat(inc.actualAmount) || 0,
        type: inc.type || 'individual',
        periodicity: inc.periodicity || 'monthly'
      }));

      // Calculate totals
      const totalNetIncome = formattedIncome.reduce((sum, inc) => 
        sum + (parseFloat(inc.netAmount) || parseFloat(inc.plannedAmount) * 0.75), 0);

      const totalPlannedIncome = formattedIncome.reduce((sum, inc) => 
        sum + parseFloat(inc.plannedAmount), 0);

      const submitData = {
        ...formData,
        categories: validCategories,
        income: formattedIncome,
        totalNetIncome,
        totalPlannedIncome,
        totalPlannedExpenses,
        period: {
          ...formData.period,
          startDate: new Date(formData.period.startDate).toISOString(),
          endDate: new Date(formData.period.endDate).toISOString()
        }
      };

      console.log('Submitting budget data:', submitData);

      if (isEditMode && budgetId) {
        await budgetService.updateBudget(budgetId, submitData);
        toast.success('Budget updated successfully');
        if (onSuccess) onSuccess();
      } else {
        await budgetService.createBudget(submitData);
        toast.success('Budget created successfully');
        if (onSuccess) onSuccess();
      }
      
      // Only navigate if we're not in inline editing mode (no onSuccess callback)
      if (!onSuccess) {
        navigate('/budgeting');
      }
    } catch (error) {
      console.error('Error submitting budget:', error);
      toast.error(error.message || 'Failed to save budget');
    }
  };

  const renderIncomeSelector = () => {
    if (!showIncomeSelector) return null;
    
    // Separate incomes and buckets
    const individualIncomes = existingIncomes.filter(item => item.type !== 'bucket');
    const buckets = existingIncomes.filter(item => item.type === 'bucket');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-5 rounded-lg shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Select Income Source</h3>
          
          {existingIncomes.length === 0 ? (
            <p className="text-gray-600 mb-4">No existing income sources found.</p>
          ) : (
            <div className="space-y-4 mb-4">
              {/* Buckets section */}
              {buckets.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold mb-2 text-indigo-800">Income Buckets</h4>
                  <div className="space-y-2">
                    {buckets.map(bucket => (
                      <div 
                        key={bucket._id}
                        onClick={() => handleSelectExistingIncome(bucket)}
                        className="p-3 border rounded-lg cursor-pointer hover:bg-indigo-50 transition-colors flex justify-between items-start"
                      >
                        <div className="flex-1">
                          <span className="font-medium text-gray-900 flex items-center">
                            <span className="text-indigo-600 mr-2">🪣</span>
                            {bucket.name}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {bucket.type || 'Bucket'} • Contains {bucket.incomesCount || 0} income sources
                            {bucket.matchingIncomes && bucket.matchingIncomes.length > 0 && (
                              <div className="mt-1 text-xs text-gray-500">
                                {bucket.matchingIncomes.map(income => 
                                  <div key={income._id} className="pl-2">
                                    • {income.name}: ${income.amount?.toFixed(2) || '0.00'}/month
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-indigo-600 font-medium">
                            ${(bucket.amount || 0).toFixed(2)}/month
                          </div>
                          <div className="text-xs text-gray-500">
                            Estimated net: ${(bucket.netAmount || 0).toFixed(2)}/month
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Individual Income Sources Section */}
              {individualIncomes.length > 0 && (
                <div>
                  <h4 className="text-base font-medium text-green-700 mb-3 pb-1 border-b border-green-100">
                    Individual Income Sources
                  </h4>
                  <div className="space-y-2">
                    {individualIncomes.map((income) => (
                      <div 
                        key={income._id || income.id || `income-${income.name}`}
                        className="border border-gray-200 p-3 rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleSelectExistingIncome(income)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-medium text-gray-800">{income.name}</span>
                            <div className="text-xs text-gray-500 mt-1">
                              {income.category || 'Income'} • {income.frequency || 'monthly'}
                              {income.bucket?.name && <span> • {income.bucket.name}</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-green-600 font-medium">
                              ${(income.amount || 0).toFixed(2)}
                            </div>
                            <div className="text-blue-600 text-sm mt-1">
                              Net: ${(income.netAmount ?? (income.amount * 0.75)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-between pt-3 border-t">
            <button
              type="button"
              onClick={() => setShowIncomeSelector(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddCustomIncome}
              className="px-4 py-2 bg-gradient-to-r from-blue-400 to-purple-500 text-white rounded hover:opacity-90"
            >
              Create Custom Income
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && isEditMode && !categoriesPopulated) {
    return <div className="flex justify-center p-8">Loading budget data...</div>;
  }

  // Wrap the return in ErrorBoundary
  return (
    <ErrorBoundary>
      <div className="p-6 bg-gradient-to-br from-blue-100 to-purple-100">
        <h1 className="text-2xl font-semibold text-gray-800 mb-8">
          {isEditMode ? 'Edit Budget' : 'Create New Budget'}
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-medium text-gray-800 mb-6">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Budget Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2.5 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="period.type" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Budget Period*
                  <span className="ml-1 text-gray-500 hover:text-gray-700 cursor-help" title="The timeframe for this budget. Monthly budgets run for one month, Yearly for an entire year, or choose Custom for a specific date range.">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                </label>
                <select
                  id="period.type"
                  name="period.type"
                  value={formData.period.type}
                  onChange={handleChange}
                  className="w-full p-2.5 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                  required
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="period.startDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date*
                </label>
                <input
                  type="date"
                  id="period.startDate"
                  name="period.startDate"
                  value={formData.period.startDate}
                  onChange={handleChange}
                  className="w-full p-2.5 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="period.endDate" className="block text-sm font-medium text-gray-700 mb-2">
                  End Date*
                </label>
                <input
                  type="date"
                  id="period.endDate"
                  name="period.endDate"
                  value={formData.period.endDate}
                  onChange={handleChange}
                  className="w-full p-2.5 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows="3"
                  className="w-full p-2.5 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                ></textarea>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPrimary"
                    name="isPrimary"
                    checked={formData.isPrimary}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 border-indigo-300 rounded"
                  />
                  <label htmlFor="isPrimary" className="ml-2 text-sm text-gray-700">
                    Set as Primary Budget
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 border-indigo-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Income Sources */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Income Sources</h2>
              <button 
                type="button"
                onClick={handleAddIncome}
                className="flex items-center gap-1 text-sm text-indigo-600 font-medium border border-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-50"
              >
                <FaPlus size={12} /> Add Income
              </button>
            </div>
            
            {formData.income.length === 0 ? (
              <div className="text-gray-500 text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
                <p>No income sources added yet.</p>
                <button 
                  type="button"
                  onClick={handleAddIncome}
                  className="bg-indigo-50 text-indigo-600 font-medium text-sm px-3 py-1.5 rounded-md mt-2 hover:bg-indigo-100"
                >
                  Select Income Sources
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="p-3 text-left text-sm font-medium text-gray-500">Name</th>
                      <th className="p-3 text-left text-sm font-medium text-gray-500">Source</th>
                      <th className="p-3 text-right text-sm font-medium text-gray-500">Amount</th>
                      <th className="p-3 text-right text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.income.map((incomeSource, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="p-3 text-gray-800">
                          <input
                            type="text"
                            name="name"
                            value={incomeSource.name}
                            onChange={(e) => handleIncomeChange(index, e)}
                            className="w-full p-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {incomeSource.bucketName ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              🪣 {incomeSource.bucketName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Individual
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            name="plannedAmount"
                            min="0"
                            step="0.01"
                            value={incomeSource.plannedAmount || 0}
                            onChange={(e) => handleIncomeChange(index, e)}
                            className="w-full p-1.5 text-right border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </td>
                        <td className="p-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveIncome(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTrash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                      <td colSpan="2" className="p-3 text-right font-medium text-gray-500">Total:</td>
                      <td className="p-3 text-right font-medium text-gray-800">
                        ${formData.income.reduce((sum, inc) => sum + parseFloat(inc.plannedAmount || 0), 0).toFixed(2)}
                      </td>
                      <td className="p-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
          
          {/* Categories Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-medium text-gray-800">Expense Categories</h2>
                <p className="text-sm text-gray-600 mt-1">Allocate how much you plan to spend in each category</p>
              </div>
              <button
                type="button"
                onClick={handleAddCategory}
                className="flex items-center bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
              >
                <FaPlus className="mr-2" /> Add Budget Category
              </button>
            </div>

            {formData.categories.length === 0 ? (
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-2">No categories added yet. Add categories to track your budget expenses.</p>
                  <p className="text-sm text-gray-600">Each category represents an area of spending (like Groceries, Rent, Entertainment) with a planned amount.</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">How Budget Categories Work:</h4>
                  <ul className="text-sm text-blue-800 list-disc pl-5 space-y-2">
                    <li>Choose a spending category (Groceries, Utilities, etc.)</li>
                    <li>Set a <strong>planned amount</strong> - how much you intend to spend</li>
                    <li>The <strong>actual amount</strong> will track your real spending</li>
                    <li>Enable <strong>rollover</strong> to carry unspent amounts to next period</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-12 gap-2 items-center mb-2 px-2">
                  <div className="col-span-3 text-xs font-medium text-gray-500">CATEGORY</div>
                  <div className="col-span-2 text-xs font-medium text-gray-500">FREQUENCY</div>
                  <div className="col-span-3 text-xs font-medium text-gray-500">PLANNED</div>
                  <div className="col-span-2 text-xs font-medium text-gray-500">ACTUAL</div>
                  <div className="col-span-1 text-xs font-medium text-gray-500 text-center flex items-center justify-center group">
                    ROLL
                    <span className="ml-1 text-gray-500 group-hover:text-gray-700 cursor-help" title="Enable rollover to carry unspent money to the next budget period. For example, if you budget $100 for Entertainment but only spend $60, the remaining $40 will be added to next period's Entertainment budget.">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                  </div>
                  <div className="col-span-1"></div>
                </div>
                
                {formData.categories.map((category, index) => (
                  <div key={`category-${index}`} className="grid grid-cols-12 gap-2 items-center">
                    {creatingCategoryIndex === index ? (
                      <>
                        <div className="col-span-10 flex items-center space-x-2">
                          <input
                            type="text"
                            value={inlineNewCategoryName}
                            onChange={(e) => setInlineNewCategoryName(e.target.value)}
                            placeholder="Enter New Category Name"
                            className="w-full p-2 bg-white border border-blue-400 rounded focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={handleSaveNewCategory}
                            className="p-2 bg-green-500 text-white rounded hover:bg-green-600"
                            disabled={loading}
                            title="Save New Category"
                          >
                           {loading ? '...' : <FaSave />}
                          </button>
                           <button
                            type="button"
                            onClick={handleCancelNewCategory}
                            className="p-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                            title="Cancel Creation"
                          >
                            <FaTimes />
                          </button>
                        </div>
                        <div className="col-span-1"></div> 
                        <div className="col-span-1"></div>
                      </>
                    ) : (
                      <>
                        <div className="col-span-3">
                          <select
                            name="categoryId"
                            value={category.categoryId || ''}
                            onChange={(e) => handleCategoryChange(index, 'categoryId', e.target.value)}
                            className="w-full p-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                            required={!category.categoryId}
                            disabled={creatingCategoryIndex !== -1}
                          >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                              <option key={cat._id || `cat-${cat.name}`} value={cat._id}>
                                {cat.name}
                              </option>
                            ))}
                            <option value="create-new" className="font-medium text-indigo-600">➕ Create New Category...</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <select
                            name="frequency"
                            value={category.frequency || 'monthly'}
                            onChange={(e) => handleCategoryChange(index, 'frequency', e.target.value)}
                            className="w-full p-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-800"
                            disabled={creatingCategoryIndex !== -1}
                          >
                            <option value="monthly">Monthly</option>
                            <option value="weekly">Weekly</option>
                            <option value="yearly">Yearly</option>
                            <option value="one-time">One-time</option>
                            <option value="bi-weekly">Bi-weekly</option>
                          </select>
                        </div>
                        <div className="col-span-3">
                          <div className="flex items-center border border-gray-300 rounded-md bg-white">
                            <span className="px-2 text-gray-600">$</span>
                            <input
                              type="number"
                              name="plannedAmount"
                              value={category.plannedAmount}
                              onChange={(e) => handleCategoryChange(index, 'plannedAmount', e.target.value)}
                              placeholder="Planned Amount"
                              min="0"
                              step="0.01"
                              className="w-full p-2 border-0 focus:ring-0 bg-white text-gray-800"
                              required
                              disabled={creatingCategoryIndex !== -1}
                            />
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="flex items-center border border-gray-300 rounded bg-white">
                            <span className="px-2 text-gray-600">$</span>
                            <input
                              type="number"
                              name="actualAmount"
                              value={category.actualAmount || ''}
                              onChange={(e) => handleCategoryChange(index, 'actualAmount', e.target.value)}
                              placeholder="Actual Amount"
                              min="0"
                              step="0.01"
                              className="w-full p-2 border-0 focus:ring-0 bg-white text-gray-800"
                              disabled={creatingCategoryIndex !== -1}
                            />
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="rollover"
                              checked={category.rollover || false}
                              onChange={(e) => handleCategoryChange(index, 'rollover', e.target.checked)}
                              className="h-4 w-4 text-indigo-600 border-indigo-300 rounded"
                              title="Enable rollover to carry unspent amounts to next period"
                              disabled={creatingCategoryIndex !== -1}
                            />
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveCategory(index)}
                            className="bg-transparent text-gray-400 hover:text-red-600 transition-colors duration-150 focus:outline-none p-1 rounded"
                            title="Remove category"
                             disabled={creatingCategoryIndex !== -1}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="font-medium">Total Planned:</span>
                    <span className="font-medium">${formData.categories.reduce((sum, cat) => sum + Number(cat.plannedAmount || 0), 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-between pt-4">
            <Link
              to="/budgeting"
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              onClick={onCancel ? (e) => { e.preventDefault(); onCancel(); } : undefined}
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="px-8 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-md hover:opacity-90 transition-opacity"
              disabled={loading || creatingCategoryIndex !== -1}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Budget' : 'Create Budget'}
            </button>
          </div>
        </form>

        {/* Income selector modal */}
        {renderIncomeSelector()}
      </div>
    </ErrorBoundary>
  );
};

export default BudgetForm;